import { useEffect, useState } from 'react';
import { Sparkles, Loader2, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import {
  adminGetBlogAutomationSettings, adminUpdateBlogAutomationSettings, adminGenerateBlogDraftNow,
} from '@/lib/api';
import { JOURNAL_AUTOMATION } from '@/constants/testIds';
import { AI_FEATURES_ENABLED } from '@/lib/featureFlags';

const CADENCE_OPTIONS = [
  { value: '2', label: 'Every 2 days' },
  { value: '3', label: 'Every 3 days' },
  { value: '7', label: 'Weekly' },
  { value: '14', label: 'Every 2 weeks' },
];

const MODE_OPTIONS = [
  { value: 'both', label: 'Autonomous + queue jumps the line' },
  { value: 'autonomous', label: 'Fully autonomous (ignore queue)' },
  { value: 'curated', label: 'Queue only (skip if empty)' },
];

const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';

const formatDate = (iso) => {
  if (!iso) return 'Not scheduled yet';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

/** Owner-only control panel for the Autonomous Blog Writer. Generates SEO
 *  Journal drafts on a cadence, grounded in Services + Gear Library — every
 *  draft lands as status="draft" and still needs an explicit Publish click
 *  from the table below. Nothing here ever goes live unreviewed. */
export const JournalAutomationCard = ({ onDraftGenerated }) => {
  const [settings, setSettings] = useState(null);
  const [queueText, setQueueText] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = () => adminGetBlogAutomationSettings()
    .then((s) => { setSettings(s); setQueueText((s.topic_queue || []).join('\n')); })
    .catch(() => setSettings({ enabled: false, cadence_days: 3, mode: 'both', topic_queue: [] }));

  useEffect(() => { load(); }, []);

  const patch = async (updates, successMsg) => {
    setSaving(true);
    try {
      const s = await adminUpdateBlogAutomationSettings(updates);
      setSettings(s);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update automation settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleQueueBlur = () => {
    const next = queueText.split('\n').map((t) => t.trim()).filter(Boolean);
    if (JSON.stringify(next) === JSON.stringify(settings?.topic_queue || [])) return;
    patch({ topic_queue: next }, 'Topic queue updated.');
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    try {
      const post = await adminGenerateBlogDraftNow();
      toast.success(`Draft ready: "${post.title}". Review it below before publishing.`);
      load();
      onDraftGenerated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Draft generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!settings) return null;

  return (
    <div data-testid={JOURNAL_AUTOMATION.card} className="mb-6 rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--lxra-gold)/0.14)] border border-[hsl(var(--lxra-gold)/0.35)]">
            <Bot className="h-4.5 w-4.5 text-[hsl(var(--lxra-gold))]" />
          </div>
          <div>
            <h4 className="font-display text-lg text-[hsl(var(--lxra-fg))]">Autonomous Blog Writer</h4>
            <p className="text-sm text-[hsl(var(--lxra-muted))] max-w-md mt-0.5">
              Drafts new SEO posts on a schedule, grounded in your Services and Gear Library. Every draft waits for your Publish click below — nothing goes live automatically.
            </p>
            {!AI_FEATURES_ENABLED && (
              <p className="text-xs text-[hsl(var(--lxra-gold))] mt-1.5" data-testid="journal-automation-paused-note">
                AI features are paused platform-wide right now — this won&apos;t generate drafts until AI is re-enabled.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            data-testid={JOURNAL_AUTOMATION.statusPill}
            className={settings.enabled
              ? 'bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal'
              : 'bg-transparent text-[hsl(var(--lxra-muted))] border border-[hsl(var(--lxra-border))] font-normal'}
          >
            {settings.enabled ? 'Active' : 'Paused'}
          </Badge>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => patch({ enabled: v }, v ? 'Autonomous drafting turned on.' : 'Autonomous drafting paused.')}
            disabled={saving}
            data-testid={JOURNAL_AUTOMATION.enableSwitch}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div className="space-y-1.5">
          <Label className={labelClass}>Cadence</Label>
          <Select value={String(settings.cadence_days)} onValueChange={(v) => patch({ cadence_days: parseInt(v, 10) })}>
            <SelectTrigger data-testid={JOURNAL_AUTOMATION.cadenceSelect} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
              {CADENCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className={labelClass}>Topic source</Label>
          <Select value={settings.mode} onValueChange={(v) => patch({ mode: v })}>
            <SelectTrigger data-testid={JOURNAL_AUTOMATION.modeSelect} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
              {MODE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5 mb-5">
        <Label className={labelClass}>Curated topic queue (one per line — jumps ahead of autonomous picks)</Label>
        <Textarea
          rows={3}
          value={queueText}
          onChange={(e) => setQueueText(e.target.value)}
          onBlur={handleQueueBlur}
          placeholder={'engagement photo locations in Georgetown\ncorporate headshot pricing guide'}
          data-testid={JOURNAL_AUTOMATION.topicQueueTextarea}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] resize-none"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[hsl(var(--lxra-border))]">
        <div className="text-xs text-[hsl(var(--lxra-muted))] space-y-0.5">
          <p data-testid={JOURNAL_AUTOMATION.lastRunText}>Last draft: {formatDate(settings.last_run_at)}</p>
          <p data-testid={JOURNAL_AUTOMATION.nextRunText}>Next scheduled: {settings.enabled ? formatDate(settings.next_run_at) : 'Paused'}</p>
        </div>
        <Button
          onClick={handleGenerateNow}
          disabled={generating}
          data-testid={JOURNAL_AUTOMATION.generateNowButton}
          variant="outline"
          className="h-10 px-4 rounded-lg border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]"
        >
          {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          Generate Draft Now
        </Button>
      </div>
    </div>
  );
};
